/** @jsxImportSource @emotion/react */
import { Box, DialogActions, useTheme } from '@mui/material'
import { reportTabs, technicalReportPopupContainer } from './TechnicalReportPopup.styles'
import { Dialog } from '@mui/material'
import { Button } from '@app/components/Atoms/Button'
import { lazy, useState } from 'react'
import { firstLetterToUpperCase } from '@app/utils/strings.utils'
const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))
import { HazardTechnicalReport } from '@app/components/Property/types'

interface TechnicalReportPopupProps {
  report: HazardTechnicalReport | null
  open: boolean
  property: string
  onClose: () => void
}

const formatReportUrl = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `https://${url}`
}

const detailsFields = ['date', 'scope', 'prepared_by', 'commissioned_by', 'purpose', 'url']

export const TechnicalReportPopup = ({
  report,
  property,
  open,
  onClose,
}: TechnicalReportPopupProps) => {
  const theme = useTheme()
  const [tab, setTab] = useState<'details' | 'summary'>('details')
  const formattedDate = report ? new Date(report.date).toLocaleDateString() : ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '880px',
          maxWidth: '880px',
        },
      }}
    >
      {report && (
        <Box css={{ padding: '48px' }}>
          <Box sx={{ p: '0', pb: '32px' }}>
            <h2
              css={{
                color: theme.palette.uintel.main,
                lineHeight: '1.4',
                paddingBottom: '8px',
              }}
            >
              {report.title}
            </h2>
            <Box
              sx={{
                fontSize: '16px',
              }}
            >
              Relating to {property}
            </Box>
          </Box>
          <Box css={reportTabs({ theme })}>
            <Button
              className={'report-tab ' + (tab == 'details' ? 'active' : '')}
              onClick={() => setTab('details')}
            >
              Details
            </Button>
            <Button
              className={'report-tab ' + (tab == 'summary' ? 'active' : '')}
              onClick={() => setTab('summary')}
            >
              Summary
            </Button>
          </Box>
          <Box sx={{ p: '0' }}>
            {tab == 'details' && (
              <Box css={technicalReportPopupContainer({ theme })}>
                {detailsFields.map((field) => {
                  if (!report[field]) return null
                  const value = field === 'date' ? formattedDate : report[field]
                  let displayField = firstLetterToUpperCase(field.replace('_', ' '))
                  if (field === 'url') {
                    displayField = 'Access the report'
                  }
                  return (
                    <Box key={field} className="report-row">
                      <Box className="report-property">{displayField}</Box>
                      <Box className="report-value">
                        {field === 'url' ? (
                          <a
                            href={formatReportUrl(report[field])}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {formatReportUrl(report[field])}
                          </a>
                        ) : (
                          value
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
            {tab == 'summary' && (
              <Box css={technicalReportPopupContainer({ theme })}>
                <UiMarkdown>{report.summary}</UiMarkdown>
              </Box>
            )}
          </Box>
          <DialogActions sx={{ p: '0' }}>
            <Button sx={{ padding: '8px 40px' }} onClick={onClose} variant="contained" color="blue">
              Close
            </Button>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  )
}
